import { useMemo, useState } from "react"
import { Canvas as R3FCanvas } from "@react-three/fiber"
import { OrbitControls } from "@react-three/drei"
import {
    Bloom,
    EffectComposer,
    Vignette,
} from "@react-three/postprocessing"
import { TOUCH } from "three"

import { Lights } from "./Lights"
import { DomoCeroEnvironment } from "./DomoCeroEnvironment"
import { ElectionPortal } from "./ElectionPortal"
import { ElectionPortalSensor } from "./ElectionPortalSensor"
import { MovementPlane } from "./MovementPlane"
import { CameraRig } from "./CameraRig"
import { ObstacleRing } from "./ObstacleRing"
import { RealStars } from "./RealStars"
import { LocalAvatar } from "../Avatar/LocalAvatar"
import { RemoteAvatars } from "../Avatar/RemoteAvatars"
import { VoidOrb } from "../Avatar/VoidOrb"
import { LinkScene } from "../Link/LinkScene"
import { useIsMobile } from "../../hooks/useDevice"
import { usePlayerStore } from "../../stores/usePlayerStore"
import { useRendererStore } from "../../stores/useRendererStore"

// 2026-05-06 — Motor dual WebGPU + fallback automático a WebGL.
//
// Estado: activación tras OPT-IN por query string (`?gpu=1` en la URL).
// Por defecto, el Canvas usa `WebGLRenderer` clásico — el camino que ya
// tenía probado todo el ecosistema del Domo (drei MeshReflectorMaterial
// del piso, Environment HDRI del entorno, EffectComposer con bloom +
// vignette). Si alguno de esos paquetes todavía no tiene
// implementación WebGPU, la rama WebGPU dejaría de pintarlos —
// resultado: pantalla negra con sólo HUD. Por eso WebGPU queda detrás
// de un flag explícito hasta que validemos que cada pieza del Domo
// tiene camino sobre el motor nuevo. Cuando lleguen los soportes
// pendientes, basta con flipear el default acá y todo el ecosistema
// pasa a WebGPU sin tocar otros archivos.
//
// Pruebas en producción: abrir la URL del cliente con `?gpu=1` para
// forzar el motor WebGPU sobre tu sesión y comparar contra el motor
// estándar. La elección persiste sólo dentro de la pestaña actual.
//
// `@react-three/postprocessing` v3 sólo maneja WebGLRenderer (su
// EffectComposer espera renderizado síncrono); por eso el bloom +
// vignette sólo se montan sobre el camino WebGL. El camino WebGPU
// confía en los emisivos nativos del Domo + Environment HDRI como
// sustituto del halo del bloom.
//
// 2026-05-07 — La elección WebGPU/WebGL ahora persiste en
// localStorage. Una vez que el Tripulante carga la app con `?gpu=1`,
// guardamos el flag y limpiamos el query string del URL bar — los
// próximos accesos via redsolarviva.com (que vienen con un JWT
// fresco en URL hash) no necesitan repetir la flag. Para volver a
// WebGL: cargar con `?gpu=0` o limpiar storage del sitio.
//
// Importante: en la práctica, las piezas del Domo (MeshReflectorMaterial,
// Environment HDRI) todavía NO renderean sobre WebGPURenderer — Drei
// no terminó esa migración. Quien active la rama WebGPU verá pantalla
// casi negra con sólo HUD + avatar. La flag se mantiene como hook de
// validación para cuando Drei suba camino completo o reemplacemos
// esos componentes por equivalentes node-material. Por ahora la rama
// estable y validada del Domo es WebGL.
const WEBGPU_STORAGE_KEY = "domo:webgpu"

function readWebGPUOptIn(): boolean {
    if (typeof window === "undefined") return false
    try {
        const params = new URLSearchParams(window.location.search)
        if (params.has("gpu")) {
            const v = params.get("gpu")
            const enable = v === "1" || v === "true"
            window.localStorage.setItem(
                WEBGPU_STORAGE_KEY,
                enable ? "1" : "0"
            )
            // Limpiamos el query del URL bar para que el history
            // quede limpio. No reload — sólo replaceState.
            const url = new URL(window.location.href)
            url.searchParams.delete("gpu")
            window.history.replaceState(
                {},
                "",
                url.pathname + url.search + url.hash
            )
            return enable
        }
        return window.localStorage.getItem(WEBGPU_STORAGE_KEY) === "1"
    } catch {
        return false
    }
}

const WEBGPU_OPT_IN = readWebGPUOptIn()
const SUPPORTS_WEBGPU =
    WEBGPU_OPT_IN &&
    typeof navigator !== "undefined" &&
    "gpu" in navigator

// Lienzo principal del Domo Cero (ex-MMSOR).
//
// Cámara orbital sobre el avatar local (sigue al moverse via CameraRig).
// MovementPlane invisible captura el tap del Tripulante (mouse o dedo)
// → setea target → predicción local + emit al cuásar.
// Bloom + vignette mantienen el lenguaje deep-space del Escáner Vibracional.
//
// Mobile: bajamos DPR a [1, 1.5] para no quemar la GPU del celular,
// reducimos las estrellas (3500 → 1500), bajamos la intensidad del bloom
// (0.85 → 0.55), bajamos la profundidad del fog (90 → 60) y abrimos el FOV
// (55 → 62) porque la pantalla angosta cierra el sentido de campo. Damping
// del OrbitControls también baja en mobile para que el dedo se sienta más
// directo. Distancias min/max se conservan — el zoom funciona con pinch.
export function Canvas() {
    const isMobile = useIsMobile()
    const phase = usePlayerStore((s) => s.phase)
    const voidStage = usePlayerStore((s) => s.voidStage)
    const cameraMode = usePlayerStore((s) => s.cameraMode)
    const isVoid = phase === "void"

    // Motor dual: estado vivo. Arranca optimista en WebGPU si el
    // navegador lo expone; si el init real falla dentro del factory,
    // el catch hace setUsingWebGPU(false) — eso re-evalúa el conditional
    // del EffectComposer y deja el camino WebGL operativo con sus
    // efectos posteriores (bloom + vignette).
    const [usingWebGPU, setUsingWebGPU] = useState(SUPPORTS_WEBGPU)

    // gl factory:
    //   · WebGL → objeto sync con parámetros estándar (R3F crea
    //     WebGLRenderer internamente).
    //   · WebGPU → factory async que instancia WebGPURenderer + init.
    //     Si init falla, atrapamos y devolvemos un WebGLRenderer
    //     creado a mano para que el Canvas no se rompa visualmente.
    const glConfig = useMemo(() => {
        const sharedParams = {
            antialias: !isMobile,
            alpha: false,
            powerPreference: "high-performance" as const,
        }
        if (!usingWebGPU) {
            // Camino WebGL clásico — registramos el modo en el store
            // así el indicador de la HUD pinta el punto azul.
            useRendererStore.getState().setMode("webgl")
            return sharedParams
        }
        return async (props: any) => {
            try {
                const { WebGPURenderer } = await import("three/webgpu")
                const renderer = new WebGPURenderer({
                    ...props,
                    ...sharedParams,
                })
                await renderer.init()
                useRendererStore.getState().setMode("webgpu")
                return renderer as any
            } catch (err) {
                console.warn(
                    "[domo] WebGPU init falló — fallback a WebGL",
                    err
                )
                setUsingWebGPU(false)
                useRendererStore.getState().setMode("webgl")
                const THREE = await import("three")
                return new THREE.WebGLRenderer({
                    ...props,
                    ...sharedParams,
                })
            }
        }
    }, [usingWebGPU, isMobile])
    // 2026-05-05 — El orbe NO se muestra durante "checking" (período en
    // que esperamos al server por una posible auto-encarnación). Solo
    // aparece cuando ya sabemos que el Tripulante necesita el rito de
    // intro. Esto evita el flash del orbe en escritorio cuando un
    // Tripulante con firma persistida se conecta.
    const showVoidOrb = isVoid && voidStage !== "checking"
    // Sprint 4 — el Domo Cero se materializa a partir de la fase
    // "descending" (cuando el haz aterrizó el Tripulante eligió forma).
    // En "void" el entorno = vacío absoluto + estrellas + orbe.
    const domoIntensity = isVoid ? 0 : 1
    const bgStyle = useMemo(
        () =>
            isVoid
                ? // Vacío absoluto — espacio profundo y frío.
                  { background: "#000000" }
                : // Domo activo — radial sutil para profundidad
                  // entre las estrellas distantes y el suelo reflector.
                  {
                      background:
                          "radial-gradient(circle at 50% 30%, #050a14 0%, #02040a 50%, #000000 100%)",
                  },
        [isVoid]
    )
    return (
        <R3FCanvas
            className="mmsor-canvas absolute inset-0"
            shadows={!isMobile}
            dpr={isMobile ? [1, 1.5] : [1, 2]}
            camera={{
                /* 2026-05-07 — Mobile recibe otro paso de acercamiento.
                   Antes [0, 1.8, 3.2] (distance 3.30) dejaba el aro
                   central visible detrás del avatar al recién encarnar.
                   Ahora [0, 1.4, 2.0] (distance ≈ 2.0) — el avatar
                   ocupa cerca del 80% del frame vertical, el aro
                   central queda detrás muy reducido y prácticamente
                   no compite con el cuerpo en el primer frame. El
                   tripulante todavía puede zoom-out con pinch hasta
                   maxDistance dinámica si quiere ver más contexto.
                   Desktop conserva [0, 2.4, 4.8]. */
                position: isMobile ? [0, 1.4, 2.0] : [0, 2.4, 4.8],
                fov: isMobile ? 62 : 55,
                near: 0.1,
                far: 200,
            }}
            gl={glConfig}
            style={bgStyle}
        >
            {/* Fog: en void, lejano y negro absoluto (las estrellas
                lejanas se desvanecen pero el orbe central se ve nítido).
                2026-05-05 v4 — En el Domo activo el fog cercano negro
                (16-130) saturaba a las estrellas detrás del biopolímero
                y cortaba la sensación de espacio profundo. Lo abrimos
                a 60-280 para que las estrellas vivas detrás del Domo
                sigan brillando incluso en distancias medias, y el
                negro recién entra como gradient lejano (si llega).
                2026-05-05 v7 — Subimos el `far` a 360 (270 mobile) para
                que la capa de estrellas (radius 200, depth 100) caiga
                completa dentro del rango visible: con 280 las estrellas
                en el extremo de la cápsula (~250m) se desvanecían casi
                a negro, dejando un cielo demasiado vacío detrás del
                ventanal del Domo. */}
            <fog
                attach="fog"
                args={
                    isVoid
                        ? ["#000000", 30, 150]
                        : [
                              "#000000",
                              isMobile ? 50 : 60,
                              isMobile ? 270 : 360,
                          ]
                }
            />
            <Lights />
            {/* v2 — Cielo verdaderamente lleno. En void el Tripulante
                debe sentir el espacio profundo absoluto, así que
                disparamos density y radius para que las estrellas
                envuelvan completamente el campo visual. En el Domo
                materializado bajamos un poco para no robar atención
                al Domo, pero seguimos generosos. Mobile mantiene un
                tope para no quemar la GPU. */}
            {/* 2026-05-05 — Counts bajados (era 14000/7500 desktop, ahora
                7000/4000) para no saturar el primer frame compile + el
                rasterizer cuando se materializa el Domo. La densidad
                visual sigue siendo amplia. */}
            {/* 2026-05-06 — Reemplazamos drei <Stars> por <RealStars>.
                Drei pinta cada estrella como sprite con shader de
                "asterisco" (5 puntas), lo que hace que el cielo se
                vea como un patrón gráfico en lugar de espacio real.
                RealStars renderea puntos circulares suaves con
                AdditiveBlending — el cielo se siente como un cielo
                real con miles de puntitos blancos de distintos
                brillos. Counts y radius respetan los mismos rangos
                que la versión anterior. */}
            <RealStars
                count={
                    isMobile
                        ? isVoid
                            ? 3500
                            : 4500
                        : isVoid
                          ? 7000
                          : 9000
                }
                radius={isVoid ? 220 : 160}
                depth={isVoid ? 120 : 90}
                size={isMobile ? 1.4 : 1.6}
            />
            {/* Capa lejana — solo durante void. Más grande, más
                tenue, simula estrellas muy distantes. */}
            {isVoid && (
                <RealStars
                    count={isMobile ? 600 : 1200}
                    radius={400}
                    depth={200}
                    size={isMobile ? 0.9 : 1.1}
                />
            )}
            <DomoCeroEnvironment
                intensity={domoIntensity}
                isMobile={isMobile}
            />
            {/* 2026-05-05 v6 — Aro cyan obstáculo que el avatar no
                puede atravesar caminando — lo cruzan saltando con S
                cuando el cuerpo gana altura suficiente. Sólo se
                pinta dentro del Domo activo (no en void). */}
            {!isVoid && <ObstacleRing />}
            {/* 2026-05-06 — Portal dorado de Reconfiguración. Al
                cruzar el aro central por su gap y entrar al círculo
                dorado, el Tripulante recibe el prompt para cambiar
                de firma. Sólo visible dentro del Domo activo. */}
            {!isVoid && <ElectionPortal />}
            {!isVoid && <ElectionPortalSensor />}
            <VoidOrb visible={showVoidOrb} />
            <MovementPlane />
            <LocalAvatar />
            {/* Sprint 6 v2 — durante void NO renderizamos peers
                remotos. La pantalla queda limpia con solo orbe +
                estrellas: "lo único que ves es tu propia consciencia
                proyectada". Vuelven cuando empezás a descender. */}
            {!isVoid && <RemoteAvatars />}
            {!isVoid && <LinkScene />}

            <OrbitControls
                makeDefault
                enabled={!isVoid && cameraMode === "thirdPerson"}
                enablePan={false}
                enableDamping
                dampingFactor={isMobile ? 0.12 : 0.08}
                /* 2026-05-07 — minDistance mobile a 1.7 para que la
                   posición inicial cercana (≈ 2.0) tenga un colchón
                   por debajo y el tripulante pueda hacer pinch-zoom
                   aún más cerca si quiere ver detalle del avatar.
                   Desktop conserva 3.2 (suficientemente cerca sin
                   meterse dentro del cuerpo). CameraRig recorta el
                   max dinámicamente cerca de la pared. */
                minDistance={isMobile ? 1.7 : 3.2}
                /* 2026-05-06 v3 — maxDistance "techo" del JSX. El
                   valor real al frame se calcula en CameraRig: si
                   el avatar está al ras del centro queda en este
                   techo, si se acerca al borde del Domo (radio 26)
                   se reduce dinámicamente para que la cámara orbital
                   nunca cruce CAMERA_SAFE_RADIUS_XZ = 27.5. */
                maxDistance={isMobile ? 13 : 11}
                /* 2026-05-06 — minPolarAngle subido de 0.25 (cámara
                   casi cenital, vista de pájaro) a π/4 (45°). Antes
                   el Tripulante podía rotar la cámara hasta casi
                   verticalmente arriba y la vista quedaba mirando
                   al techo del Domo (zona oscura, sin geometría
                   iluminada hacia abajo) → contribuía al rectángulo
                   negro al solicitar enlaces. Con π/4 la cámara se
                   queda en la zona "media-alta" del orbital,
                   siempre apuntando al avatar con perspectiva
                   diagonal. maxPolarAngle baja un poco también
                   para no rasar el piso. */
                minPolarAngle={Math.PI / 4}
                maxPolarAngle={Math.PI / 2.2}
                /* Touch nativo de OrbitControls: ONE dedo rota la cámara
                   (cambiar el ángulo de visión), DOS dedos hacen pinch para
                   zoom (DOLLY_PAN sin PAN porque enablePan=false). El
                   movimiento del avatar lo decide MovementPlane via tap. */
                touches={
                    isMobile
                        ? { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_PAN }
                        : undefined
                }
                /* Velocidad de rotación más amable al dedo (más despacio
                   que con mouse). */
                rotateSpeed={isMobile ? 0.55 : 1}
                zoomSpeed={isMobile ? 0.7 : 1}
            />
            <CameraRig />

            {/* 2026-05-05 — Bloom mucho más sutil. El antes era 0.85
                desktop / 0.55 mobile con threshold 0.25 → cualquier
                emissive captaba bloom y el conjunto blanco del Domo
                + reflector + Environment generaba un resplandor que
                tapaba todo. Ahora pequeño halo solo en píxeles muy
                brillantes (threshold 0.6) y intensidad bajada. Las
                estrellas siguen brillando, los LEDs se ven, pero el
                Domo no se vuelve un blanco quemado.
                2026-05-06 — EffectComposer condicional al camino
                WebGL. `@react-three/postprocessing` v3 maneja sólo
                WebGLRenderer; sobre WebGPURenderer no compone el
                pase y deja la pantalla en negro. La rama WebGPU
                usa los emisivos nativos del Domo + Environment HDRI
                como sustituto de bloom. */}
            {!usingWebGPU && (
                <EffectComposer enabled={!isMobile}>
                    <Bloom
                        intensity={isMobile ? 0.25 : 0.4}
                        luminanceThreshold={0.6}
                        luminanceSmoothing={0.9}
                        mipmapBlur
                    />
                    <Vignette eskil={false} offset={0.25} darkness={0.7} />
                </EffectComposer>
            )}
        </R3FCanvas>
    )
}
